import { useRef, useState } from 'react';
import { Upload, Loader2, X, Image as ImageIcon, GripVertical } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { Button } from './ui/button';

/**
 * Multi-image uploader that pushes each file straight to Cloudinary via the
 * `/upload/image` backend endpoint. Replaces the older "paste URL" flow that
 * caused broken galleries when users typed text or hotlink-blocked URLs.
 */
export default function MultiImageUpload({
  value = [],
  onChange,
  folder = 'general',
  max = 9,
  label = 'Subir fotos',
  testIdPrefix = 'multi-img',
}) {
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const items = Array.isArray(value) ? value : [];
  const remaining = Math.max(0, max - items.length);

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    const slice = files.slice(0, remaining);
    if (files.length > remaining) {
      toast.warning(`Solo se subirán ${remaining} foto(s). Máx ${max} en total.`);
    }
    setUploading(true);
    try {
      const uploaded = [];
      for (const file of slice) {
        if (!['image/jpeg', 'image/png', 'image/webp', 'image/gif'].includes(file.type)) {
          toast.error(`Formato inválido: ${file.name}`);
          continue;
        }
        if (file.size > 10 * 1024 * 1024) {
          toast.error(`Imagen muy grande (>10MB): ${file.name}`);
          continue;
        }
        const fd = new FormData();
        fd.append('file', file);
        try {
          const res = await api.post(`/upload/image?folder=${folder}`, fd, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
          if (res.data?.secure_url) uploaded.push(res.data.secure_url);
        } catch (err) {
          toast.error(`Error subiendo ${file.name}`);
        }
      }
      if (uploaded.length > 0) {
        onChange([...items, ...uploaded]);
        toast.success(`${uploaded.length} foto(s) subida(s)`);
      }
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const removeAt = (idx) => {
    const next = [...items];
    next.splice(idx, 1);
    onChange(next);
  };

  const move = (from, to) => {
    if (to < 0 || to >= items.length) return;
    const next = [...items];
    const [m] = next.splice(from, 1);
    next.splice(to, 0, m);
    onChange(next);
  };

  return (
    <div className="space-y-3" data-testid={`${testIdPrefix}-container`}>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        multiple
        onChange={handleFiles}
        className="hidden"
        disabled={uploading || remaining === 0}
      />

      {/* Grid de previews */}
      {items.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {items.map((url, idx) => (
            <div
              key={`${url}-${idx}`}
              className="relative group aspect-square rounded-xl overflow-hidden bg-zinc-900 border border-zinc-800"
              data-testid={`${testIdPrefix}-item-${idx}`}
            >
              <img
                src={url}
                alt={`Foto ${idx + 1}`}
                className="w-full h-full object-cover"
                onError={(e) => {
                  // Hide broken images entirely (legacy garbage urls)
                  e.target.style.display = 'none';
                  e.target.parentElement.classList.add('border-red-500/40');
                  const note = e.target.parentElement.querySelector('.broken-note');
                  if (note) note.classList.remove('hidden');
                }}
              />
              <div className="broken-note hidden absolute inset-0 flex items-center justify-center text-[11px] text-red-300 px-2 text-center">
                Imagen rota. Eliminala y subila de nuevo.
              </div>
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/60 transition-colors flex items-center justify-center gap-1 opacity-0 group-hover:opacity-100">
                <button
                  type="button"
                  onClick={() => move(idx, idx - 1)}
                  className="bg-white/90 text-black rounded-md px-1.5 py-1 text-[10px] font-bold hover:bg-white"
                  title="Subir orden"
                  disabled={idx === 0}
                  data-testid={`${testIdPrefix}-up-${idx}`}
                >↑</button>
                <button
                  type="button"
                  onClick={() => move(idx, idx + 1)}
                  className="bg-white/90 text-black rounded-md px-1.5 py-1 text-[10px] font-bold hover:bg-white"
                  title="Bajar orden"
                  disabled={idx === items.length - 1}
                  data-testid={`${testIdPrefix}-down-${idx}`}
                >↓</button>
                <button
                  type="button"
                  onClick={() => removeAt(idx)}
                  className="bg-red-500 hover:bg-red-600 text-white rounded-md px-2 py-1 text-[10px] font-bold flex items-center gap-1"
                  data-testid={`${testIdPrefix}-remove-${idx}`}
                >
                  <X size={11} /> Eliminar
                </button>
              </div>
              <div className="absolute top-1.5 left-1.5 bg-black/60 backdrop-blur-sm text-white text-[10px] px-1.5 py-0.5 rounded font-bold flex items-center gap-1">
                <GripVertical size={10} /> {idx + 1}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Upload button */}
      {remaining > 0 ? (
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className="w-full rounded-xl border-2 border-dashed border-zinc-700 hover:border-[#D4AF37]/50 bg-zinc-900/50 hover:bg-zinc-900 transition-all flex flex-col items-center justify-center gap-2 p-6"
          data-testid={`${testIdPrefix}-upload-btn`}
        >
          {uploading ? (
            <Loader2 className="w-7 h-7 text-zinc-400 animate-spin" />
          ) : (
            <>
              <div className="w-11 h-11 rounded-full bg-zinc-800 flex items-center justify-center">
                <ImageIcon className="w-5 h-5 text-zinc-400" />
              </div>
              <span className="text-sm text-zinc-300 font-medium">{label}</span>
              <span className="text-xs text-zinc-500">
                Podés seleccionar varias · {remaining} restante{remaining === 1 ? '' : 's'} · JPG, PNG, WebP, GIF · máx 10MB c/u
              </span>
            </>
          )}
        </button>
      ) : (
        <p className="text-xs text-zinc-500 text-center py-2">
          Llegaste al máximo de {max} fotos. Eliminá alguna para subir otra.
        </p>
      )}
    </div>
  );
}
